import { useEffect, useMemo } from "react";
import { fileUrl } from "@/lib/api";

// Detects constrained networks/devices once per session.
export const useNetworkProfile = () => useMemo(() => {
  const c = navigator.connection || {};
  const slow = c.saveData || ["slow-2g", "2g", "3g"].includes(c.effectiveType) || (c.downlink && c.downlink < 1.5);
  const lowMem = navigator.deviceMemory && navigator.deviceMemory <= 2;
  return { lowBandwidth: !!slow, radius: slow || lowMem ? 1 : 2 };
}, []);

export const pickSrc = (page, pxWidth, lowBandwidth) => fileUrl(lowBandwidth || pxWidth * (window.devicePixelRatio || 1) <= 900 ? page.sm : page.lg);

// In-memory page cache (blob URLs): pages survive re-renders even when the CDN sends no-store.
const blobs = new Map();
const MAX = 220;
export const cachedUrl = (src) => blobs.get(src)?.url || null;
export const loadImage = (src) => {
  const hit = blobs.get(src);
  if (hit) { blobs.delete(src); blobs.set(src, hit); return hit.promise; }
  const entry = { url: null };
  entry.promise = fetch(src).then((r) => { if (!r.ok) throw new Error(r.status); return r.blob(); }).then(async (b) => {
    const url = URL.createObjectURL(b);
    const img = new Image();
    img.src = url;
    await img.decode().catch(() => {});
    entry.url = url;
    return url;
  });
  entry.promise.catch(() => blobs.delete(src));
  blobs.set(src, entry);
  while (blobs.size > MAX) { const [k, e] = blobs.entries().next().value; blobs.delete(k); if (e.url) setTimeout(() => URL.revokeObjectURL(e.url), 60000); }
  return entry.promise;
};

// Tiny thumbnails for every page, so a placeholder is always ready instantly.
export const usePreloadThumbs = (pages) => useEffect(() => { (pages || []).forEach((p) => p.thumb && loadImage(fileUrl(p.thumb)).catch(() => {})); }, [pages]);

// Sliding preload window around the current page (nearest first).
export const usePagePreloader = (pages, current, pxWidth, { lowBandwidth, radius }) => {
  useEffect(() => {
    if (!pages?.length || !pxWidth) return;
    const order = [0, 1];
    for (let d = 1; d <= radius + 1; d++) order.push(d + 1, -d);
    order.forEach((d) => { const p = pages[current + d]; if (p) loadImage(pickSrc(p, pxWidth, lowBandwidth)).catch(() => {}); });
  }, [pages, current, pxWidth, lowBandwidth, radius]);
};
